import React, { MouseEventHandler, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import ImageGallery from 'components/Luxkit/Carousel/ImageGallery'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import styled from 'styled-components'
import { rem } from 'polished'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import OfferBadge from 'components/Common/OfferBadge'
import { isBedbankOffer, isCruiseOffer, isLEOffer, isTourV2Offer } from 'lib/offer/offerTypes'
import { mediaQueryUp } from 'components/utils/breakpoint'
import variables from 'styles/vendor/bootstrap/variables'
import { ImageCarouselInterstitial } from 'components/Luxkit/Carousel/BaseImageCarousel'
import VimeoVideo, { VimeoPlayerOptions } from 'components/Common/Video/VimeoVideo'
import Clickable from 'components/Common/Clickable'
import ModalContext from 'contexts/ModalContext'
import VideoLightbox from 'components/Luxkit/Carousel/VideoLightbox'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage'
import ImageGalleryLightbox from 'components/Luxkit/Carousel/ImageGalleryLightbox'
import TextButton from 'components/Luxkit/Button/TextButton'
import SolidPlayIcon from 'components/Luxkit/Icons/solid/SolidPlayIcon'
import LineImagesIcon from 'components/Luxkit/Icons/line/LineImagesIcon'
import { take } from 'lib/array/arrayUtils'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Image from 'components/Common/Image'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton, { ExperienceBookmarkButton, TourBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import ShareModal from 'components/Common/Modals/ShareModal'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import TourProductTypeLabel from 'components/Tours/TourProductTypeLabel'
import { useInView } from 'react-intersection-observer'
import MasterModalContext from 'contexts/MasterModalContext'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import SolidStarCircleIcon from 'components/Luxkit/Icons/solid/SolidStarCircleIcon'
import { isTourLimitedTimeOffer } from 'lib/tours/tourUtils'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'

const VimeoWrapper = styled(Clickable)`
  height: 100%;
  width: 100%;
  position: relative;
  background-color: ${props => props.theme.palette.neutral.default.one};

  > * {
    pointer-events: none;
  }
`

const VimeoWrapperBackground = styled.div`
  height: 100%;
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.default.one};
`

const MobileHuggingContainer = styled(LayoutContainer)`
  padding: 0;

  ${mediaQueryUp.tablet} {
    padding-right: ${rem(variables.gutterWidth / 2)};
    padding-left: ${rem(variables.gutterWidth / 2)};
  }
`

const OfferOverlayContainer = styled(LabelGroup)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const FloatingOfferBadge = styled(OfferBadge)`
  margin: ${rem(16)};
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 50% 50%;
  gap: 8px;
  aspect-ratio: 2.78;
  position: relative;

  > :first-child,
  > ${FloatingOfferBadge} {
    grid-column: 1 / 2;
    grid-row: 1 / 3;
  }

  > ${FloatingOfferBadge} {
    align-self: start;
    justify-self: end;
  }
`

const GalleryButtons = styled(Group)`
  display: flex;
  pointer-events: auto;
  gap: ${rem(8)};
  justify-content: flex-end;
  position: absolute;
  bottom: ${rem(16)};
  right: ${rem(16)};
`

const OverlayLabelContainer = styled(LabelGroup)`
  position: absolute;
  top: ${rem(16)};
  left: ${rem(16)};
`

const TopRightActionContainer = styled(Group)`
  position: absolute;
  pointer-events: auto;
  top: ${rem(12)};
  right: ${rem(12)};
`

const StyledLayoutContainer = styled(LayoutContainer)`
  padding-top: 0;
  ${mediaQueryUp.tablet}{
    padding-top: ${rem(16)};
  }
`

const defaultImageProps: ImageParams = { tabletWidth: '400px', quality: 'good' }
const mainImageProps: ImageParams = { tabletWidth: '800px', quality: 'best' }
const carouselImageParams: ImageParams = { mobileAspectRatio: '16:9', tabletAspectRatio: '16:9', quality: 'good' }
const loopQuery: VimeoPlayerOptions = { loop: true, autoplay: true, controls: true }
const mutedLoopQuery: VimeoPlayerOptions = { muted: true, loop: true, controls: true }

interface GalleryView {
  images: Array<App.Image>;
  videoIds: Array<string>;
  productType?: App.ProductType;
  additionalLabels?: React.ReactNode;
  badge?: App.OfferBadge;
}

interface Props {
  offer?: App.AnyOffer;
  tourVariation?: App.Tours.TourV2OfferVariation;
  experience?: App.ExperienceOffer;
  shareModalConfig?: ShareConfig;
  /**
   * Will set the video as the first slide and
   * auto-play it if there is one on the offer
   *
   * NOTE: Currently disabled
   */
  autoPlayVideo?: boolean;
}

/**
 * An offer page specific gallery that will show the images/videos for the given
 * offer types supported
 */
function OfferPageGallery(props: Props) {
  const { offer, experience, tourVariation, shareModalConfig } = props
  const [hasAutoPlayed, setHasAutoPlayed] = useState(true)
  const [hasAutoPaused, setHasAutoPaused] = useState(false)
  const isMobileDevice = useIsMobileDevice()
  const showLightbox = useContext(ModalContext)
  const modalState = useContext(MasterModalContext)
  const videoRef = useRef<any>(null)
  const [ref, inView] = useInView()
  /**
   * Auto play video currently turned off as there as a massive spike in
   * bandwidth usage and they are asking for many dolldaridoos
   */
  const autoPlayVideo = isTourV2Offer(offer)

  const {
    images,
    videoIds,
    badge,
    productType,
    additionalLabels,
  } = useMemo((): GalleryView => {
    if (isTourV2Offer(offer)) {
      return {
        videoIds: offer.video ? [offer.video.id] : [],
        images: tourVariation?.images ?? [],
        additionalLabels: <LabelGroup>
          {isTourLimitedTimeOffer(offer) &&
            <StructureLabel startIcon={<SolidStarCircleIcon />} variant="exclusive">Limited Time Lux Exclusive</StructureLabel>
          }
          <TourProductTypeLabel productType={offer.productType} />
        </LabelGroup>,
      }
    } else if (experience) {
      return {
        videoIds: experience.video ? [experience.video] : [],
        images: experience.images,
        productType: undefined,
        badge: undefined,
      }
    } else if (isLEOffer(offer)) {
      let productType: App.ProductType | undefined = offer.productType
      if (isLPCTacticalEnabled() && offer.hasTactical) {
        productType = 'limited_time_special'
      } else if (offer.isPartnerProperty) {
        productType = undefined
      }

      return {
        videoIds: offer.vimeoVideoId ? [offer.vimeoVideoId] : [],
        badge: offer.badge,
        images: offer.images,
        productType,
        additionalLabels: <>
          {offer.availabilitySchedule?.end && <OfferUrgencyCountdownLabel endDate={offer.availabilitySchedule.end } tagPlace="offer-gallery"/>}
          <OfferPartnershipLabel offer={offer} />
        </>,
      }
    } else if (isBedbankOffer(offer)) {
      return {
        videoIds: offer.videos?.filter(vid => vid.type === 'vimeo').map(vid => vid.id) ?? [],
        badge: undefined,
        images: offer.images,
        productType: offer.promotions.length > 0 ? undefined : offer.productType,
      }
    } else if (isCruiseOffer(offer)) {
      return {
        images: offer.images,
        productType: offer.productType,
        badge: undefined,
        videoIds: [],
      }
    }
    return {
      images: [],
      videoIds: [],
    }
  }, [experience, offer, tourVariation])

  const videoOnPause = useCallback(() => {
    if (!hasAutoPaused) {
      setHasAutoPlayed(false)
    }
    setHasAutoPaused(false)
  }, [hasAutoPaused, setHasAutoPlayed, setHasAutoPaused])

  const carouselInterstitials: Array<ImageCarouselInterstitial> | undefined = useMemo(() => {
    if (videoIds.length > 0) {
      const position = autoPlayVideo ? 1 : 2
      return [
        ...videoIds.map((videoId, index) => ({
          id: 'video',
          position: position + index,
          element: (<>
            {!autoPlayVideo && <VimeoWrapper onClick={() => showLightbox(<VideoLightbox videoId={videoId} playOnMount query={loopQuery} />)}>
              <VimeoVideo vimeoVideoId={videoId} query={mutedLoopQuery} />
            </VimeoWrapper>}
            {autoPlayVideo && <VimeoWrapperBackground ref={ref}>
              <VimeoVideo vimeoVideoId={videoId} ref={videoRef} onPaused={videoOnPause} query={mutedLoopQuery} />
            </VimeoWrapperBackground>}
          </>),
        })),
      ]
    }

    return undefined
  }, [videoIds, autoPlayVideo, ref, videoOnPause, showLightbox])

  const gridInterstitials : Array<ImageCarouselInterstitial> | undefined = useMemo(() => {
    if (!autoPlayVideo && videoIds.length > 0) {
      return [
        ...videoIds.map((videoId, index) => ({
          id: 'video',
          position: 2 + index,
          element: <VimeoVideo vimeoVideoId={videoId} query={mutedLoopQuery} />,
        })),
      ]
    }

    return undefined
  }, [autoPlayVideo, videoIds])

  const showVideoLightbox = useCallback(() => {
    if (videoIds.length > 0) {
      showLightbox(<VideoLightbox videoId={videoIds[0]} playOnMount query={loopQuery} />)
    }
  }, [videoIds, showLightbox])

  const [galleryMode, galleryImages] = useMemo<[] | ['grid', Array<App.Image>] | ['carousel', Array<App.Image>]>(() => {
    if (!images.length) return []

    if (!isMobileDevice && images.length >= 3) {
      const imageToShow = autoPlayVideo && !!videoIds.length ? 2 : 3
      return ['grid', take(images, imageToShow)]
    }

    return ['carousel', images]
  }, [images, isMobileDevice, autoPlayVideo, videoIds.length],
  )

  useEffect(() => {
    if (autoPlayVideo && hasAutoPlayed) {
      if (inView && !modalState.open) {
        videoRef.current?.play()
      } else {
        setHasAutoPaused(true)
        videoRef.current?.pause()
      }
    }
  }, [inView, offer, hasAutoPlayed, modalState, autoPlayVideo])

  const handleImageClick = useCallback<MouseEventHandler<HTMLPictureElement>>((event) => {
    const imageIndex = Number(event.currentTarget.dataset.index) || 0

    showLightbox(<ImageGalleryLightbox
      images={images}
      defaultIndex={imageIndex}
      interstitials={galleryMode === 'grid' ? gridInterstitials : undefined}
    />)
  }, [showLightbox, images, galleryMode, gridInterstitials])

  return <Pane>
    <StyledLayoutContainer>
      {galleryMode === 'grid' &&
        <GridContainer>
          {autoPlayVideo && !!videoIds.length && <VimeoWrapperBackground ref={ref}>
            <VimeoVideo vimeoVideoId={videoIds[0]} ref={videoRef} onPaused={videoOnPause} query={mutedLoopQuery} />
          </VimeoWrapperBackground>}
          {galleryImages.map((image, imageIndex) =>
            <Clickable
              key={image.id ?? image.url}
              onClick={handleImageClick}
              data-index={videoIds.length && imageIndex !== 0 ? imageIndex + 1 : imageIndex}
            >
              {image.id && <ResponsiveImage
                id={image.id}
                fit="center"
                {...(imageIndex === 0 ? mainImageProps : defaultImageProps)}
              />}
              {image.url && <Image
                image={image}
                fit="center"
              />}
            </Clickable>)}
          <OverlayLabelContainer>
            {productType && <ProductTypeLabel productType={productType} />}
            {additionalLabels}
          </OverlayLabelContainer>
          <GalleryButtons direction="horizontal">
            {!autoPlayVideo && videoIds.length > 0 &&
              <TextButton
                kind="secondary"
                variant="dark"
                startIcon={<SolidPlayIcon />}
                onClick={showVideoLightbox}
              >
                Play video
              </TextButton>
            }
            <CSSBreakpoint min="desktop">
              {images.length > 1 &&
                <TextButton
                  kind="secondary"
                  variant="dark"
                  startIcon={<LineImagesIcon />}
                  onClick={handleImageClick}
                >
                  View gallery
                </TextButton>
              }
            </CSSBreakpoint>
          </GalleryButtons>
          {badge && <FloatingOfferBadge badge={badge} />}
        </GridContainer>
      }
    </StyledLayoutContainer>
    {galleryMode === 'carousel' &&
      <MobileHuggingContainer>
        <ImageGallery
          imageParams={carouselImageParams}
          topLeft={<OfferOverlayContainer>
            {productType && <ProductTypeLabel productType={productType} />}
            {additionalLabels}
          </OfferOverlayContainer>}
          topRight={<>
            {shareModalConfig && <CSSBreakpoint max="mobile">
              <TopRightActionContainer direction="horizontal" gap={8}>
                <ShareModal shareConfig={shareModalConfig}>
                  <ShareWithFriendsButton iconOnly />
                </ShareModal>
                {offer && (
                  <TripGuard>
                    {isTourV2Offer(offer) && <TourBookmarkButton offer={offer} selectedVariation={tourVariation} withLabel="never" />}
                    {!isTourV2Offer(offer) && <BookmarkButton offer={offer} withLabel="never" />}
                  </TripGuard>
                )}
                {experience && (
                  <TripGuard>
                    <ExperienceBookmarkButton offer={experience} withLabel="never" />
                  </TripGuard>
                )}
              </TopRightActionContainer>
            </CSSBreakpoint>}
            {badge && <CSSBreakpoint min="desktop">
              <OfferBadge badge={badge} />
            </CSSBreakpoint>}
          </>}
          interstitials={carouselInterstitials}
          images={galleryImages}
          eagerLoadFirstImage
          arrowBehaviour="always-visible"
        />
      </MobileHuggingContainer>
    }
  </Pane>
}

export default OfferPageGallery
